<template>
  <tbody v-if="this.search">
    <tr>
      <td style="width: 55px">
        <font-awesome-icon v-if="!completedBatch"
          title="Match Process not yet complete."
          class="fa-lg has-text-warning"
          :icon="['fas', 'exclamation-triangle']"
        />
      </td>
      <td class="">
        <div class="is-flex flex-direction--column">
          <div class="is-flex">
            <router-link
              v-if="hasModule('PRSID:SRCH:OPTIONS:VIEW') && !isRejected"
              :to="{ path: `file/${search.id}/details` }"
              class="has-text-weight-bold"
            >
              {{ search.id }}
            </router-link>
            <span v-if="isRejected">{{ search.id }}</span>
            <span v-if="search.fileName"
              class="padding-left--small has-text-grey max-width-15"
            >
              {{ search.fileName }}
            </span>
          </div>
          <div class="is-flex has-text-grey">
            {{ search.submissionTypeCode }}
          </div>
        </div>
      </td>
      <td :class="{'is-hidden-touch': isMobile}">
        <div
          class="is-flex flex-direction--column"
          v-if="search.pidSearchDistrictSchoolAssociation.length"
        >
          <div v-if="isMultiDistrict">
            <b style="font-size:14px">
              Multiple {{ getFieldLabel(`PER:${personType}:SEARCH:DISTRICT_CODE`) }}(s/es)
            </b>
          </div>
          <div v-else>
            <router-link
              v-if="search.pidSearchDistrictSchoolAssociation[0].district
                && search.pidSearchDistrictSchoolAssociation[0].district.location"
              :to="{
                name: 'PIDistrictInfoDetails',
                params: {
                  code: search.pidSearchDistrictSchoolAssociation[0].districtCode,
                },
              }"
              class="has-text-weight-bold has-text-primary"
              style="font-size:14px"
            >
              {{ search.pidSearchDistrictSchoolAssociation[0].district.location.name }}
            </router-link>
            <span class="padding-left--small has-text-grey is-italic">
              ({{ search.pidSearchDistrictSchoolAssociation[0].districtCode }})
            </span>
          </div>
          <div v-if="isMultiSchool">
            <span class="is-italic has-text-grey">
              Multiple {{ getFieldLabel(`PER:${personType}:SEARCH:SCHOOL_CODE`) }}(s/es)
            </span>
          </div>
          <div v-else>
            <router-link
              v-if="search.pidSearchDistrictSchoolAssociation[0].school
                && search.pidSearchDistrictSchoolAssociation[0].school.location"
              :to="{
                name: 'PISchoolInfoDetails',
                params: {
                  districtCode: search.pidSearchDistrictSchoolAssociation[0].districtCode,
                  schoolCode: search.pidSearchDistrictSchoolAssociation[0].schoolCode,
                },
              }"
              class="is-italic"
            >
              {{ search.pidSearchDistrictSchoolAssociation[0].school.location.name }}
            </router-link>
            <span class="padding-left--small has-text-grey is-italic">
              ({{ search.pidSearchDistrictSchoolAssociation[0].schoolCode }})
            </span>
          </div>
        </div>
      </td>
      <td>
        <div class="is-flex flex-direction--column">
          <strong class="is-flex">
            {{
              new Intl.DateTimeFormat('en', {
                year:'numeric',
                month:'2-digit',
                day:'2-digit',
              })
              .format(new Date(search.createdDate))
            }}
          </strong>
          <span class="has-text-grey">
            {{
              new Intl.DateTimeFormat('en', {
                hour:'numeric',
                minute:'numeric',
              })
              .format(new Date(search.createdDate))
            }}
          </span>
        </div>
      </td>
      <td>
        <div class="has-text-weight-bold">
          {{ recordInfoColumn }} of {{ search.recordCount }}
        </div>
        <div v-if="!completedBatch" class="has-text-grey">
          PENDING
        </div>
      </td>
      <td :class="{'is-hidden-mobile': isMobile}">
        <pid-status-info-icon
          :header="false"
          :processStatus="search.status"
        />
      </td>
      <td>
        <div class="is-flex flex-justify--space-between flex-align--center">
          <div>
            <es-loader v-if="reloading" size="2x"/>
            <a
              v-else-if="isInternalAction && !action.isHidden"
              @click="handleInternalCall"
              @keypress="handleInternalCall"
              tabindex="0"
              role="button"
              :aria-label="action.text"
              :title="action.text"
              :class="{'is-hidden-mobile': isMobile}"
            >
              {{ action.text }}
            </a>
            <router-link v-else-if="!action.isHidden"
              :to="{ path: action.link }"
              :title="action.text || 'Actions'"
              :class="{'is-hidden-mobile': isMobile}"
            >
              {{ action.text }}
            </router-link>
          </div>
          <a v-if="activeMenu.length">
            <es-menu :setValues="activeMenu" @callMenuAction="callMenuAction"/>
          </a>
        </div>
      </td>
    </tr>
  </tbody>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faExclamationTriangle,
} from '@fortawesome/pro-solid-svg-icons';
import PidStatusInfoIcon from '@/components/PidStatusInfoIcon.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsMenu from '@/components/EsMenu.vue';
import PermissionManager from '@/mixins/PermissionManager';
import FieldLabels from '@/mixins/FieldLabels';
import NotificationManager from '@/mixins/NotificationManager';

library.add(
  faExclamationTriangle,
);

export default {
  mixins: [
    PermissionManager,
    FieldLabels,
    NotificationManager,
  ],
  props: [
    'search',
    'isMobile',
  ],
  components: {
    EsLoader,
    EsMenu,
    PidStatusInfoIcon,
  },
  data() {
    return {
      reloading: null,
      personType: null,
    };
  },
  created() {
    this.setDataElements();
  },
  computed: {
    ...mapGetters('lookups', [
      'pidNearMatchesTransGroup',
      'defaultPersonType',
    ]),
    action() {
      // TODO: update links when Near Match page is completed.
      if (['LOADED', 'QUEUED', 'VALIDATION_IN_PROGRESS', 'VALIDATION_COMPLETE',
        'RUNNING_MATCHING', 'ERRORED', 'ERRORED_MATCHING', 'ERRORED_VALIDATION'].includes(this.search.status)) {
        // this is an internal link to call some function.
        return {
          isInternal: true,
          link: '/personid',
          internalAction: 'refreshSearchRow',
          text: 'Refresh',
          isHidden: !this.hasModule('PRSID:SRCH:OPTIONS:HIST:SRCH'),
        };
      }
      return {
        isHidden: true,
      };
    },
    activeMenu() {
      return this.searchCompletedMenu.filter(item => !item.isHidden);
    },
    isRejected() {
      return (this.search.status === 'REJECTED');
    },
    searchCompletedMenu() {
      const menuItems = [];
      if (['LOADED', 'QUEUED', 'VALIDATION_IN_PROGRESS', 'VALIDATION_COMPLETE',
        'RUNNING_MATCHING', 'ERRORED', 'ERRORED_MATCHING', 'ERRORED_VALIDATION']
        .includes(this.search.status) && this.action.text === 'Refresh') {
        menuItems.push({
          id: 0,
          description: 'Refresh',
          isHidden: !this.hasModule('PRSID:SRCH:OPTIONS:HIST:SRCH'),
          isInternal: true,
          callMenuAction: 'refreshSearchRow',
        });
      }
      if (['COMPLETED', 'COMPLETED_WITH_PENDING', 'VALIDATION_CANCELED'].includes(this.search.status)) {
        menuItems.push({
          id: 1,
          description: 'Download',
          path: `/personId/search/file/${this.search.id}/download`,
          // isHidden: !this.hasModule('PRSID:DWNLD:DOWNLOAD') || !this.isDownloadTypesAllowed,
        });
      }
      if (!this.isRejected) {
        menuItems.push({
          id: 2,
          description: 'View Search Details',
          path: `file/${this.search.id}/details`,
          isHidden: !this.hasModule('PRSID:SRCH:OPTIONS:VIEW'),
        });
      }
      return menuItems;
    },
    completedBatch() {
      const { status } = this.search;
      if (status === 'COMPLETED'
        || status === 'VALIDATION_CANCELED') {
        return true;
      }
      return false;
    },
    isInternalAction() {
      if (this.action.isInternal) {
        return true;
      }
      return false;
    },
    isMultiDistrict() {
      if (!this.search.pidSearchDistrictSchoolAssociation) {
        return false;
      }
      const setOfUniqueDistricts = this.search.pidSearchDistrictSchoolAssociation
        .map(a => a.districtCode)
        .slice()
        .sort((a, b) => a > b)
        .reduce((a, b) => {
          if (a.slice(-1)[0] !== b) a.push(b);
          return a;
        }, []);
      if (setOfUniqueDistricts.length > 1) {
        return true;
      }
      return false;
    },
    isMultiSchool() {
      if (!this.search.pidSearchDistrictSchoolAssociation) {
        return false;
      }
      const setOfUniqueSchools = this.search.pidSearchDistrictSchoolAssociation
        .map(a => a.schoolCode)
        .slice()
        .sort((a, b) => a > b)
        .reduce((a, b) => {
          if (a.slice(-1)[0] !== b) a.push(b);
          return a;
        }, []);
      if (setOfUniqueSchools.length > 1) {
        return true;
      }
      return false;
    },
    recordInfoColumn() {
      const statusOfSearch = this.search.status;
      switch (statusOfSearch) {
        case 'ERRORED_MATCHING':
        case 'RUNNING_MATCHING': {
          if (this.search.pidSearchTransStatusCountAssociation.length) {
            const otherRecords = this.search.pidSearchTransStatusCountAssociation
              .map(item => item.statusCount)
              .reduce((prev, curr) => prev + curr, 0);
            const displayRecords = this.search.recordCount - otherRecords;
            return displayRecords;
          }
          return this.search.recordCount;
        }
        default:
          return this.search.recordCount;
      }
    },
    allowedExtractDownloadTypes() {
      return this.$store.state.propertiesModule.properties['PERSON-EXTRACT_DOWNLOAD_TYPES_ALLOWED'];
    },
    isDownloadTypesAllowed() {
      if (this.allowedExtractDownloadTypes) {
        return this.allowedExtractDownloadTypes.includes(this.search.submissionTypeCode);
      }
      return false;
    },
  },
  methods: {
    callMenuAction(menuAction) {
      if (menuAction && menuAction.doAction) {
        this[menuAction.doAction]();
      } else {
        // If no specific action is passed, assume refresh
        this.refreshSearchRow();
      }
    },
    handleInternalCall() {
      this[this.action.internalAction]();
    },
    async refreshSearchRow() {
      try {
        this.reloading = true;
        const res = await axios.get(`person-search/by-file/${this.search.id}`);
        const updated = res.data;
        if (updated) {
          this.$emit('update:search', updated);
        }
      } catch (e) {
        const errorMsg = (e.response && e.response.data && e.response.data.message)
          || 'Failed to refresh search. Please try again.';
        this.showNotification('danger', [errorMsg]);
      } finally {
        this.reloading = false;
      }
    },
    setDataElements() {
      this.personType = this.search.personTypeCode;
    },
  },
};
</script>

<style lang="sass" scoped>
  .padding-left--small
    padding-left: 0.25rem
  .max-width-15
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis
    max-width: 20ch
</style>
